import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';

const AdminUsersStats = ({ selectedSchool, users, isSuperAdmin }) => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    newUsers: 0,
    totalLessonsCompleted: 0,
    last7Days: {
      newUsers: 0,
      activeUsers: 0,
    },
  });

  const [selectedMetric, setSelectedMetric] = useState('all');
  const [showDetails, setShowDetails] = useState(false);
  const [chartData, setChartData] = useState({
    series: [],
    options: {
      chart: {
        type: 'line',
        height: 350,
        toolbar: {
          show: false,
        },
      },
      stroke: {
        curve: 'smooth',
        width: 2,
      },
      xaxis: {
        type: 'datetime',
        labels: {
          style: {
            colors: '#6B7280',
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: '#6B7280',
          },
        },
      },
      tooltip: {
        theme: 'light',
        x: {
          format: 'MMM dd, yyyy',
        },
      },
      colors: ['#3B82F6', '#10B981', '#8B5CF6'],
      grid: {
        borderColor: '#E5E7EB',
        strokeDashArray: 4,
      },
    },
  });

  // Calculate statistics from users
  const calculateStats = (users) => {
    if (!users.length) return;

    const stats = {
      totalUsers: users.length,
      activeUsers: 0,
      newUsers: 0,
      totalLessonsCompleted: 0,
      last7Days: {
        newUsers: 0,
        activeUsers: 0,
      },
    };

    // Calculate last 7 days metrics
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    // Track unique active users
    const activeUsers = new Set();
    const newUsers = new Set();

    users.forEach((user) => {
      const userCreatedDate = new Date(user.created_at);
      const lastActiveDate = new Date(user.last_login_at || user.created_at);

      // Count new users in last 7 days
      if (userCreatedDate >= sevenDaysAgo) {
        stats.last7Days.newUsers++;
        newUsers.add(user.user_id);
      }

      // Count active users in last 7 days
      if (lastActiveDate >= sevenDaysAgo) {
        stats.last7Days.activeUsers++;
        activeUsers.add(user.user_id);
      }

      // Count total lessons completed
      if (user.completed_lessons) {
        stats.totalLessonsCompleted += user.completed_lessons.length;
      }
    });

    stats.activeUsers = activeUsers.size;
    stats.newUsers = newUsers.size;

    setStats(stats);
  };

  // Prepare chart data
  const prepareChartData = (users) => {
    if (!users.length) return;

    // Group users by date and metrics
    const userGroups = users.reduce((acc, user) => {
      const date = new Date(user.created_at).toISOString().split('T')[0];
      if (!acc[date]) {
        acc[date] = {
          newUsers: 0,
          activeUsers: 0,
          lessonsCompleted: 0,
        };
      }
      acc[date].newUsers++;
      if (new Date(user.last_login_at || user.created_at) >= new Date(date)) {
        acc[date].activeUsers++;
      }
      if (user.completed_lessons) {
        acc[date].lessonsCompleted += user.completed_lessons.length;
      }
      return acc;
    }, {});

    // Create series data
    const series = [
      {
        name: 'New Users',
        data: Object.entries(userGroups).map(([date, metrics]) => ({
          x: new Date(date).getTime(),
          y: metrics.newUsers,
        })),
      },
      {
        name: 'Active Users',
        data: Object.entries(userGroups).map(([date, metrics]) => ({
          x: new Date(date).getTime(),
          y: metrics.activeUsers,
        })),
      },
      {
        name: 'Lessons Completed',
        data: Object.entries(userGroups).map(([date, metrics]) => ({
          x: new Date(date).getTime(),
          y: metrics.lessonsCompleted,
        })),
      },
    ];

    // Filter series based on selected metric
    const filteredSeries =
      selectedMetric === 'all'
        ? series
        : series.filter((s) => s.name.toLowerCase().includes(selectedMetric.toLowerCase()));

    setChartData((prev) => ({
      ...prev,
      series: filteredSeries,
    }));
  };

  useEffect(() => {
    calculateStats(users);
    prepareChartData(users);
  }, [users, selectedMetric]);

  if (!selectedSchool) return null;

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">User Analytics</h2>
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 flex items-center"
        >
          {showDetails ? 'Hide Details' : 'Show Details'}
          <svg
            className={`w-5 h-5 ml-2 transform transition-transform duration-200 ${showDetails ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Stats Grid Section */}
      <div
        className={`${showDetails ? 'bg-gray-50' : ''} rounded-lg ${showDetails ? 'p-6' : 'p-2'} mb-6`}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Total Users Card */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-blue-100 text-blue-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Users</p>
                <div className="flex items-baseline">
                  <p className="text-xl font-semibold text-gray-700">{stats.totalUsers}</p>
                  <p className="ml-2 text-xs text-gray-500">
                    (+{stats.last7Days.newUsers} last 7d)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Active Users Card */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-green-100 text-green-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Active Users</p>
                <div className="flex items-baseline">
                  <p className="text-xl font-semibold text-gray-700">{stats.activeUsers}</p>
                  <p className="ml-2 text-xs text-gray-500">(last 7d)</p>
                </div>
              </div>
            </div>
          </div>

          {/* Total Lessons Card */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 rounded-full bg-purple-100 text-purple-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500">Total Lessons Completed</p>
                <p className="text-xl font-semibold text-gray-700">{stats.totalLessonsCompleted}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Sections */}
      {showDetails && (
        <>
          {/* Timeline Chart Section */}
          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-700">User Activity Timeline</h3>
              <select
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value)}
                className="p-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-gray-700 bg-white"
              >
                <option value="all">All metrics</option>
                <option value="new">New Users</option>
                <option value="active">Active Users</option>
                <option value="lessons">Lessons Completed</option>
              </select>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <ReactApexChart
                options={chartData.options}
                series={chartData.series}
                type="line"
                height={350}
              />
            </div>
          </div>

          {/* User Details Table - Only show for super admins */}
          {isSuperAdmin && (
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">User Details</h3>
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created At
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Login
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lessons Completed
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.length > 0 ? (
                      [...users]
                        .sort((a, b) => {
                          const dateA = new Date(a.last_login_at || a.created_at);
                          const dateB = new Date(b.last_login_at || b.created_at);
                          return dateB - dateA;
                        })
                        .map((user) => (
                          <tr key={user.user_id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {user.user_id}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {user.email}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {new Date(user.created_at).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {new Date(user.last_login_at || user.created_at).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {user.completed_lessons ? user.completed_lessons.length : 0}
                            </td>
                          </tr>
                        ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                          No users found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminUsersStats;
